import apolloClient from 'apollo-client'
import {
  CHANGE_ORDER_STATUS,
  COMPLETE_CASH_ORDER_REFUND,
  COMPLETE_COMPLIANCE_POS_TERMINAL_ORDER_REFUND,
  COMPLETE_EDIT_ORDER,
  COMPLETE_EDIT_ORDER_BY_PICKUP,
  COMPLETE_EDIT_ORDER_BY_SHIPPING,
  COMPLETE_ORDER_REFUND,
  COMPLETE_POS_TERMINAL_ORDER_REFUND,
  CREATE_ORDER_EDIT,
  CREATE_ORDER_REFUND,
  DELETE_ORDER_EDIT_ITEM,
  GET_ORDER_TRANSACTIONS,
  GET_POS_REFUND_DISCOUNTS_LIST,
  GET_PROMO_DISCOUNTS_LIST,
  ORDER_ADDRESS_PROFILE_UPDATE,
  ORDER_EDIT_DISCOUNT_APPLY,
  ORDER_EDIT_WINE_CLUB_DISCOUNT_APPLY,
  REMOVE_ORDER_EDIT_DISCOUNT,
  RESET_ORDER_REFUND_EDIT,
  SAVE_ORDER_EDIT_DISCOUNT,
  UPDATE_EDIT_ORDER,
  UPDATE_EDIT_ORDER_NOTES,
  UPDATE_EDIT_ORDER_TAX_AND_DISCOUNT_REMOVED,
  UPDATE_ORDER_EDIT,
  UPDATE_ORDER_EDIT_PATCH,
  UPDATE_ORDER_WITH_ITEMS,
  UPDATE_REFUND_ORDER_WITH_ITEMS,
  VALIDATE_DISCOUNT_CODE,
  VALIDATE_ORDER_EDIT_DISCOUNT,
} from './graphql'
import {getTimezone} from 'app/utilities'
import {Order} from 'app/features/orders/models/EditOrderState'
import {RefundOrder} from 'app/features/orders/models/OrderRefundState'
import GraphqlService from '../GraphqlService'

class OrderEditService extends GraphqlService {
  createOrderEdit = async (orderId: string) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: CREATE_ORDER_EDIT,
      variables: {orderId},
    })
    if (errors) {
      throw errors
    }
    return data.createEditOrder.orderEdits[0]
  }

  createOrderRefund = async (orderId: string, fulfillmentId: string) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: CREATE_ORDER_REFUND,
      variables: {orderId, fulfillmentId},
    })
    if (errors) {
      throw errors
    }
    return data.createRefundOrder.orderEdits[0]
  }

  updateOrderEditPatch = async (id: string, params: any) => {
    const {errors} = await apolloClient.mutate({
      mutation: UPDATE_ORDER_EDIT_PATCH,
      variables: {id, patch: params},
    })
    if (errors) {
      throw errors
    }
  }

  updateOrderEditItems = async (orderEdit: any) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: UPDATE_ORDER_WITH_ITEMS,
      variables: {orderEdit},
    })
    if (errors) {
      throw errors
    }
    return data.updateOrderEditWithItems.orderEditItems
  }

  updateRefundOrderEditItems = async (orderRefund: any) => {
    const {errors} = await apolloClient.mutate({
      mutation: UPDATE_REFUND_ORDER_WITH_ITEMS,
      variables: {orderRefund},
    })
    if (errors) {
      throw errors
    }
  }

  updateEditOrder = async (orderEdit: any) => {
    const {errors} = await apolloClient.mutate({
      mutation: UPDATE_EDIT_ORDER,
      variables: {orderEdit},
    })
    if (errors) {
      throw errors
    }
  }

  updateOrderEdit = async (id: string, order: any) => {
    const {errors} = await apolloClient.mutate({
      mutation: UPDATE_ORDER_EDIT,
      variables: {id, order},
    })
    if (errors) {
      throw errors
    }
  }

  applyDiscountCode = async (discount: any) => {
    discount.timezone = getTimezone()
    const {data, errors} = await apolloClient.mutate({
      mutation: ORDER_EDIT_DISCOUNT_APPLY,
      variables: {discount},
    })
    if (errors) {
      throw errors
    }
    return data.orderEditDiscountCodeApply
  }

  applyWineClubDiscount = async (discount: any) => {
    discount.timezone = getTimezone()
    const {data, errors} = await apolloClient.mutate({
      mutation: ORDER_EDIT_WINE_CLUB_DISCOUNT_APPLY,
      variables: {discount},
    })
    if (errors) {
      throw errors
    }
    return data.orderEditAutoDiscountApply
  }

  validateOrderEditDiscount = async (discount: any) => {
    discount.timezone = getTimezone()
    discount.lineItems = mapToLineItemModel(discount.lineItems)
    const {data, errors} = await apolloClient.mutate({
      mutation: VALIDATE_ORDER_EDIT_DISCOUNT,
      variables: {discount},
    })
    if (errors) {
      throw errors
    }
    return data.validateOrderEditDiscount
  }

  validateDiscountCode = async (discount: any) => {
    discount.timezone = getTimezone()
    discount.lineItems = mapToLineItemModel(discount.lineItems)
    const {data, errors} = await apolloClient.mutate({
      mutation: VALIDATE_DISCOUNT_CODE,
      variables: {discount},
    })
    if (errors) {
      throw errors
    }
    return data.validateDiscountCode
  }

  completeEditOrder = async (order: Order | RefundOrder, isShippingRate: boolean) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_EDIT_ORDER,
      variables: {orderEdit: mapToCompleteOrderEditModel(order, isShippingRate)},
    })
    if (errors) {
      throw errors
    }
    return data.completeEditOrder
  }

  completeEditOrderByShipping = async (order: Order) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_EDIT_ORDER_BY_SHIPPING,
      variables: {orderEdit: mapToCompleteOrderEditModel(order, !!order.shippingRate)},
    })
    if (errors) {
      throw errors
    }
    return data.completeEditOrderByShipping
  }

  completeEditOrderByPickup = async (order: Order) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_EDIT_ORDER_BY_PICKUP,
      variables: mapToEditOrderModel(order),
    })
    if (errors) {
      throw errors
    }
    return data.completeEditOrderByPickup
  }

  completeOrderRefund = async (order: RefundOrder) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_ORDER_REFUND,
      variables: mapToEditOrderModel(order),
    })
    if (errors) {
      throw errors
    }
    return data.completeOrderPartialRefund
  }

  completeCashOrderRefund = async (order: RefundOrder) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_CASH_ORDER_REFUND,
      variables: mapToEditOrderModel(order),
    })
    if (errors) {
      throw errors
    }
    return data.completeCashOrderPartialRefund
  }

  //POS terminal order partial refund

  //Fetch POS order payment transaction and amount to refund
  getPosRefundOrder = async (order: RefundOrder) => {
    const {data, errors} = await apolloClient.query({
      query: GET_POS_REFUND_DISCOUNTS_LIST,
      variables: mapToEditOrderModel(order),
    })
    if (errors) {
      throw errors
    }
    return data.posRefundOrder
  }

  //Complete POS Order partial refund
  completePosTerminalOrderPartialRefund = async (order: any, paymentIntern: any) => {
    const model = mapToCompletedPosOrderRefundModel(order, paymentIntern)
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_POS_TERMINAL_ORDER_REFUND,
      variables: {
        model,
      },
    })
    if (errors) {
      throw errors
    }
    return data.completePosTerminalOrderPartialRefund
  }

  //Complete POS Order partial refund
  completeCompliancePosTerminalOrderPartialRefund = async (order: RefundOrder) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: COMPLETE_COMPLIANCE_POS_TERMINAL_ORDER_REFUND,
      variables: {
        order: {
          orderId: order.id,
          orderEditId: order.orderEditId,
        },
      },
    })
    if (errors) {
      throw errors
    }
    return data.completePosTerminalOrderPartialRefund
  }

  updateEditOrderNotes = async (orderEditId: string, note: Record<string, any>) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: UPDATE_EDIT_ORDER_NOTES,
      variables: {orderEditId, note},
    })
    if (errors) {
      throw errors
    }
    return data.updateOrderEditNotes.notes
  }

  removeOrderEditDiscount = async (discountCodeId: string) => {
    const {errors} = await apolloClient.mutate({
      mutation: REMOVE_ORDER_EDIT_DISCOUNT,
      variables: {discountCodeId},
    })
    if (errors) {
      throw errors
    }
  }

  saveOrderEditDiscount = async (discount: any) => {
    const {data, errors} = await apolloClient.mutate({
      mutation: SAVE_ORDER_EDIT_DISCOUNT,
      variables: {discount},
    })
    if (errors) {
      throw errors
    }
    const {discounts} = data.saveOrderEditDiscount
    return discounts
  }

  getDiscountList = async () => {
    const {data, errors} = await apolloClient.query({
      query: GET_PROMO_DISCOUNTS_LIST,
    })
    if (errors) {
      throw errors
    }
    return data.getPromoCodeDiscounts.nodes
  }

  changeOrderStatus = async (params: any) => {
    await apolloClient.mutate({
      mutation: CHANGE_ORDER_STATUS,
      variables: params,
    })
  }

  async getOrderTransactions(orderId: string) {
    const {data, errors} = await apolloClient.query({
      query: GET_ORDER_TRANSACTIONS,
      variables: {orderId},
    })
    if (errors) {
      throw errors
    }
    const {nodes} = data.getOrderTransactions
    return nodes[0]
  }

  async resetOrderRefundEdit(id: string) {
    const {data, errors} = await this._query(RESET_ORDER_REFUND_EDIT, {id})
    if (errors) {
      throw errors
    }
    const {orderEdits} = data.resetOrderRefundEdit
    if (orderEdits && orderEdits.length > 0) {
      return orderEdits[0]
    }
    return
  }

  updateEditOrderTaxAndAutoDiscountRemoved = async (param: any) => {
    await apolloClient.mutate({
      mutation: UPDATE_EDIT_ORDER_TAX_AND_DISCOUNT_REMOVED,
      variables: {order: param, id: param.id},
    })
  }

  deleteEditOrderItem = async (id: string) => {
    await apolloClient.mutate({
      mutation: DELETE_ORDER_EDIT_ITEM,
      variables: {id},
    })
  }

  orderAddressProfileUpdate = async (id: string) => {
    await apolloClient.mutate({
      mutation: ORDER_ADDRESS_PROFILE_UPDATE,
      variables: {id},
    })
  }
}

const orderEditService = new OrderEditService()
export default orderEditService

const mapToEditOrderModel = (order: any) => {
  return {
    orderEdit: {
      orderId: order.id,
      orderEditId: order.orderEditId,
    },
  }
}

const mapToCompletedPosOrderRefundModel = (order: any, paymentIntent: any) => {
  return {
    order: {
      orderId: order.orderId,
      orderEditId: order.orderEditId,
      amount: order.amount,
      transactionId: order.transactionId,
    },
    paymentIntent,
  }
}

const mapToLineItemModel = (items: any[]) => {
  return items.map((item: any) => {
    return {
      productId: item.productId,
      variantId: item.variantId,
      price: parseFloat(item.price),
      quantity: parseInt(item.quantity),
    }
  })
}

export const mapToCompleteOrderEditModel = (
  {id, orderEditId, shippingRate, shippingRateId, isFreeShipping}: Order | any,
  isShippingRate: boolean
) => {
  const model: any = {
    orderEditId,
    orderId: id,
  }
  if (shippingRate && isShippingRate && !isFreeShipping) {
    model.shippingRate = mapToShippingRate(shippingRate, shippingRateId)
  }
  return model
}

export const mapToShippingRate = (shippingMethod: any, id: any) => {
  return {
    id,
    serviceId: shippingMethod.serviceId,
    price: shippingMethod.rate ? shippingMethod.rate : shippingMethod.price,
    title: shippingMethod.serviceName,
    serviceType: shippingMethod.serviceType,
    carrierType: shippingMethod.carrierType,
    originalPrice: shippingMethod?.originalPrice || shippingMethod?.originalRate,
    discount: shippingMethod.discount,
    handlingFee: shippingMethod?.handlingFee,
    providerShippingPrice: shippingMethod?.providerShippingPrice,
  }
}
